import classNames from 'classnames';
import React from 'react';
import type { DonutDataItem } from 'redux/modules/superreports.types';
import { cssVariables } from 'styles';
import type { Currency } from 'types/account';
import styles from './DonutWidget.module.scss';
import { getColor } from './helpers';

interface ItemProps {
  activeIndex?: number;
  color: string;
  currency?: Currency;
  index: number;
  name: string;
  subHeading?: string;
  value: number;
  handleMouseOver: (index: number) => void;
  renderValue: (value: number, className: string, currency?: Currency) => JSX.Element;
}

const DonutWidgetListItem: React.FC<ItemProps> = ({
  activeIndex,
  color,
  currency,
  index,
  name,
  value,
  handleMouseOver,
  renderValue,
  subHeading,
}) => (
  <li
    onMouseOver={() => handleMouseOver(index)}
    className={classNames(styles.item, {
      [styles.active]: activeIndex === index,
    })}
    data-tooltipindex={index}
  >
    <span className={styles.dot} style={{ background: color, color }} />
    <span className={styles.label} title={name}>
      {name}
      {subHeading && subHeading !== name && (
        <span className={styles.subHeading} title={subHeading}>
          ({subHeading})
        </span>
      )}
    </span>
    {renderValue(value, styles.value, currency)}
  </li>
);

interface Props {
  activeIndex?: number;
  currency?: Currency;
  data: Array<DonutDataItem>;
  handleMouseOver: (index: number) => void;
  handleMouseLeave: React.MouseEventHandler;
  renderValue: (value: number, className: string, currency?: Currency) => JSX.Element;
}

const DonutWidgetListItems = React.forwardRef<HTMLOListElement, Props>(
  ({ activeIndex, currency, data, handleMouseLeave, handleMouseOver, renderValue }, ref) => {
    let accumulatedPercent = 0;
    return (
      <ol onMouseLeave={handleMouseLeave} ref={ref}>
        {data.map(({ name, value, mergedItems, color, percentOfTotal, subHeading }, index) => {
          const colorPercent = accumulatedPercent + percentOfTotal / 2;
          const itemColor = (Array.isArray(color) ? color[0] : color) ?? getColor(colorPercent);
          accumulatedPercent += percentOfTotal;
          return (
            <React.Fragment key={`${name}-${index}`}>
              {!mergedItems && (
                <DonutWidgetListItem
                  name={name}
                  subHeading={subHeading}
                  value={value}
                  index={index}
                  activeIndex={activeIndex}
                  color={itemColor}
                  handleMouseOver={handleMouseOver}
                  renderValue={renderValue}
                  currency={currency}
                />
              )}
              {mergedItems &&
                mergedItems.map(({ name: mergedName, value: mergedValue, subHeading }) => (
                  <DonutWidgetListItem
                    key={`${mergedName}-${mergedValue}`}
                    subHeading={subHeading}
                    name={mergedName}
                    value={mergedValue}
                    index={index}
                    activeIndex={activeIndex}
                    color={cssVariables.widgetOtherColor}
                    handleMouseOver={handleMouseOver}
                    renderValue={renderValue}
                    currency={currency}
                  />
                ))}
            </React.Fragment>
          );
        })}
      </ol>
    );
  }
);

export default React.memo(DonutWidgetListItems);
